import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { getArticle, ARTICLES } from "@/lib/blogData";
import { useSEO } from "@/lib/seo";
import { ArrowRight, Info } from "lucide-react";

function Block({ block }) {
  if (block.t === "h2") return <h2 className="font-serif font-normal text-2xl sm:text-3xl tracking-tight text-espresso mt-12 mb-3 leading-snug">{block.v}</h2>;
  if (block.t === "ul") return (
    <ul className="my-5 space-y-2.5">
      {block.v.map((li, i) => (
        <li key={i} className="flex gap-3 text-espresso-soft leading-relaxed">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-olive shrink-0" /> {li}
        </li>
      ))}
    </ul>
  );
  if (block.t === "callout") return (
    <div className="my-7 flex gap-3 rounded-2xl bg-olive-light border border-warmborder p-5">
      <Info size={18} className="text-olive shrink-0 mt-0.5" />
      <p className="text-sm text-espresso leading-relaxed">{block.v}</p>
    </div>
  );
  return <p className="text-espresso-soft leading-relaxed my-4 text-[17px]">{block.v}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useSEO({
    title: article ? `${article.title} — PaySignal` : "PaySignal Journal",
    description: article?.excerpt,
    image: article?.image,
    type: "article",
    canonical: window.location.origin + "/blog/" + slug,
    jsonLd: article && {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.image,
      "datePublished": article.date,
      "dateModified": article.date,
      "articleSection": article.category,
      "author": { "@type": "Organization", "name": article.author },
      "publisher": { "@type": "Organization", "name": "PaySignal" },
      "mainEntityOfPage": window.location.origin + "/blog/" + slug,
    },
  });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) return <Navigate to="/blog" replace />;
  const more = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-cream text-espresso">
      <SiteHeader />

      <article className="max-w-2xl mx-auto px-6 pt-14 pb-16">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-olive">{article.category}</span>
        <h1 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 text-espresso leading-[1.12]" data-testid="post-title">{article.title}</h1>
        <p className="text-warmmuted text-sm mt-5 font-num">{article.author} · {article.readTime}</p>
        <img src={article.image} alt={article.title} className="w-full h-64 sm:h-80 object-cover rounded-[1.5rem] mt-7 border border-warmborder" />
        <div className="mt-6">
          {article.content.map((b, i) => <Block key={i} block={b} />)}
        </div>

        <div className="mt-14 rounded-[2rem] bg-espresso p-8 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-12 w-56 h-56 rounded-full bg-olive/25 blur-3xl" />
          <div className="absolute -bottom-12 -left-10 w-44 h-44 rounded-full bg-lime/15 blur-3xl" />
          <h3 className="relative font-serif font-light text-2xl sm:text-3xl tracking-tight text-cream">Track what you're owed — free.</h3>
          <p className="relative text-cream/70 mt-3 text-sm">One calm ledger for invoices and platform payouts.</p>
          <Link to="/signup"><Button className="relative mt-6 rounded-full px-7 h-11 bg-lime text-espresso hover:bg-lime-dark hover:text-espresso" data-testid="post-cta">Start tracking <ArrowRight size={16} className="ml-1" /></Button></Link>
        </div>
      </article>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h2 className="font-serif font-normal text-2xl tracking-tight text-espresso mb-6">Keep reading</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {more.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`related-${a.slug}`}
              className="group border border-warmborder rounded-[1.5rem] bg-white overflow-hidden hover:shadow-[0_30px_70px_-55px_rgba(42,36,33,0.5)] hover:-translate-y-1 transition-all duration-300">
              <div className="h-32 overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">{a.category}</span>
                <h3 className="font-serif font-medium text-base text-espresso mt-1.5 leading-snug">{a.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
